import type { BridgeMessage, BridgeReply } from '../../models/bridge-message';
import { isBridgeWebEventType } from '../../models/bridge-web-event';

/**
 * `null` para qualquer coisa que não seja uma mensagem nossa — e isso é a regra, não a exceção.
 *
 * **Todo `postMessage` da página cai no mesmo `onMessage` da casca.** Bibliotecas de terceiro,
 * extensões e o próprio React DevTools falam por esse canal; a casca vê tudo. Confiar no
 * formato aqui significa passar objeto arbitrário de origem desconhecida para um handler
 * nativo, que é o buraco de segurança óbvio de uma ponte de WebView.
 *
 * Por isso a validação é estrutural e o `type` é conferido contra a lista fechada de
 * capacidades, em vez de aceitar qualquer string.
 */
export function bridgeParse(raw: unknown): BridgeMessage | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;

  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Record<string, unknown>;

  if (typeof candidate.id !== 'string' || candidate.id.length === 0) return null;
  if (!isBridgeWebEventType(candidate.type)) return null;

  return {
    id: candidate.id,
    type: candidate.type,
    payload: candidate.payload ?? null,
  };
}

/** O mesmo, para o sentido casca → web. */
export function bridgeParseReply(raw: unknown): BridgeReply | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;

  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Record<string, unknown>;

  if (typeof candidate.id !== 'string' || candidate.id.length === 0) return null;
  if (typeof candidate.ok !== 'boolean') return null;

  return {
    id: candidate.id,
    ok: candidate.ok,
    payload: candidate.payload ?? null,
    error: typeof candidate.error === 'string' ? candidate.error : null,
  };
}
